import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { Loading } from '@carbon/react';
import { useAuthSession } from 'src/hooks/useAuthSession';
import { useOnboardingStatus } from 'src/hooks/useOnboardingStatus';
import BranchStep from './BranchStep';
import InviteStep from './InviteStep';
import OrganizationStep from './OrganizationStep';
import ProfileStep from './ProfileStep';
import RestaurantStep from './RestaurantStep';
import StepRail from './StepRail';
import VeilPanel from './VeilPanel';
import { isProfileSkipped, skipProfile } from './profileSkip';
import { WIZARD_STEPS, currentStep } from './wizardStep';

const Onboarding = () => {
  const { t } = useTranslation('authentication');
  const { session, user, isPending, isVerified } = useAuthSession();
  const { data: status, isLoading: statusPending, refetch } = useOnboardingStatus(Boolean(session));
  const [profileSkipped, setProfileSkipped] = useState(isProfileSkipped);

  if (isPending || statusPending) {
    return <Loading withOverlay description={t('onboarding.loading')} />;
  }
  if (!session) {
    return <Navigate to="/sign-in" replace />;
  }
  if (!isVerified) {
    return <Navigate to="/verify-email" replace />;
  }
  if (!status) {
    return <Loading withOverlay description={t('onboarding.loading')} />;
  }

  const profileComplete = Boolean(user?.name) || profileSkipped;
  const step = currentStep(status, profileComplete);
  const stepIndex = WIZARD_STEPS.indexOf(step);
  const onAdvance = () => refetch();
  const onSkipProfile = () => {
    skipProfile();
    setProfileSkipped(true);
  };

  return (
    <div className="onboarding">
      <aside className="onboarding__rail" data-testid="onboarding-rail">
        <div className="onboarding__brand">
          <img src="/restavo.svg" alt="Restavo logo" />
          <span>Restavo</span>
        </div>

        {/* currentIndex is derived from the server status on every render, so the rail
            reflects real progress and cannot drift out of step with the form. */}
        <StepRail currentIndex={stepIndex} />
      </aside>

      <main className="onboarding__centre" data-testid="onboarding-centre">
        {step === 'profile' && <ProfileStep onDone={onAdvance} onSkip={onSkipProfile} />}
        {step === 'organization' && <OrganizationStep onDone={onAdvance} />}
        {step === 'restaurant' && <RestaurantStep onDone={onAdvance} />}
        {step === 'branch' && <BranchStep onDone={onAdvance} />}
        {step === 'invite' && <InviteStep status={status!} />}
      </main>

      <VeilPanel />
    </div>
  );
};

export default Onboarding;
